import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import type { StyledMantineProps } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/styledMantine.types';
import styled from 'styled-components';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { Box, Center, Group, Loader } from '@mantine/core';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	DS_AI_MOTION,
	DS_AI_SUCCESS,
} from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.tokens';
import {
	fadeRise,
	tickIn,
} from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.animations';
import { PROCESSING_ICON_SIZE } from './ProcessingCard.styles';
import type { ProcessingSubstepView } from './ProcessingCard.types';

interface Props {
	substep: ProcessingSubstepView;
}

export const ProcessingSubstepItem = ({ substep }: Props) => {
	const tCapManage = useTranslationCapManage();
	const isDone = substep.state === 'done';

	return (
		<SubstepReveal
			component="li"
			display="grid"
			data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_PROCESSING_SUBSTEP}
			data-state={substep.state}
		>
			<Group
				component="span"
				gap={4}
				wrap="nowrap"
				mih={0}
				c="gray.9"
				fz="0.813rem"
				fw={400}
				lh="1.125rem"
				style={{ overflow: 'hidden' }}
			>
				<Center
					component="span"
					inline
					w={PROCESSING_ICON_SIZE}
					h={PROCESSING_ICON_SIZE}
					flex="none"
					aria-label={
						isDone
							? tCapManage('dealstack_ai.ingest.processing.substep_done')
							: tCapManage('dealstack_ai.ingest.processing.substep_running')
					}
				>
					{isDone ? (
						<SubstepTick
							key="done"
							component="span"
							display="inline-flex"
							c={DS_AI_SUCCESS.text}
						>
							<Icon variant="tick-circle-filled" size="lg" />
						</SubstepTick>
					) : (
						<Loader key="running" type="oval" size={14} color="blue.6" />
					)}
				</Center>
				{tCapManage(substep.label.key, substep.label.values)}
			</Group>
		</SubstepReveal>
	);
};

/** A substep, which rises into place as it appears. */
const SubstepReveal = styled(Box)<StyledMantineProps<'li'>>({
	gridTemplateRows: '1fr',
	animation: `${fadeRise} ${DS_AI_MOTION.slowMs}ms ${DS_AI_MOTION.easeOut} both`,
});

/** A substep's tick, drawn as the substep completes. */
const SubstepTick = styled(Box)<StyledMantineProps<'span'>>({
	animation: `${tickIn} ${DS_AI_MOTION.tickMs}ms ${DS_AI_MOTION.easeOut} both`,
});
